import classNames from "classnames";
import {
  type ButtonHTMLAttributes,
  forwardRef,
  type ForwardRefExoticComponent,
  type RefAttributes,
} from "react";
import { LoadingDots } from "../icons";
import styles from "./Button.module.css";
import Tooltip from "./Tooltip";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  onClick?: () => void;
  variant?:
    | "primary"
    | "secondary"
    | "link"
    | "link_dark"
    | "icon"
    | "icon_only"
    | "error"
    | "success";
  size?: "normal" | "small" | "mini";
  icon?: JSX.Element;
  iconPosition?: "left" | "right";
  disabled?: boolean;
  disabledTooltip?: string;
  loading?: boolean;
  preventDefault?: boolean;
}

const buildStyle = (variant: Props["variant"], size: Props["size"]) => `
  ${styles.button} 
  ${variant ? styles[variant] : ``} 
  ${size ? styles[size] : ``}
  `;

const Button: ForwardRefExoticComponent<
  Props & RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement>(
  (
    {
      className = "",
      variant = "primary",
      size = "normal",
      onClick,
      disabled,
      disabledTooltip,
      icon,
      iconPosition = "left",
      loading,
      children,
      preventDefault,
      ...rest
    }: Props,
    ref
  ) => {
    const style = buildStyle(variant, size);
    const isIconButton = ["icon", "icon_only"].includes(variant);

    const iconClasses = classNames({
      "flex m-ds-1": variant === "icon",
      "ml-ds-1 w-ds-4 h-ds-4 flex align-center items-center": !isIconButton,
      "mr-ds-3": !isIconButton && iconPosition === "left",
      "ml-ds-3": !isIconButton && iconPosition === "right",
    });

    const ButtonComponent = (
      <button
        {...rest}
        ref={ref}
        onClick={(e) => {
          e.stopPropagation();
          if (preventDefault) e.preventDefault();
          if (disabled || loading) return;
          onClick?.();
        }}
        disabled={loading || disabled}
        className={`${style} ${loading ? styles.loading : ``} ${
          disabledTooltip ? styles.disabledTooltip : ``
        } ${className}`}
      >
        {loading && (
          <div className={`w-6 mr-2`}>
            <LoadingDots />
          </div>
        )}
        {!loading && icon && iconPosition === "left" && (
          <div className={iconClasses}>{icon}</div>
        )}
        {children}
        {icon && iconPosition === "right" && (
          <div className={iconClasses}>{icon}</div>
        )}
      </button>
    );

    return disabled && disabledTooltip ? (
      <Tooltip placement={"bottom"} trigger={ButtonComponent}>
        {disabledTooltip}
      </Tooltip>
    ) : (
      ButtonComponent
    );
  }
);

Button.displayName = "Button";

export default Button;
